<template>
  <div class="whatsapp-instance-page">
    <!-- Cabeçalho -->
    <div class="header">
      <h1>
        <i class="fab fa-whatsapp"></i>
        Configuração de Instâncias WhatsApp
      </h1>
      <p class="subtitle">Crie e gerencie instâncias do Evolution API para enviar mensagens</p>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Conteúdo -->
    <div v-show="!loading" class="instance-content">
      <!-- Botão Criar Instância -->
      <div class="action-bar">
        <button 
          v-if="!editingInstance"
          type="button" 
          class="btn-primary" 
          @click="createInstance()"
        >
          <i class="fa-solid fa-plus"></i>
          Nova Instância
        </button>
      </div>

      <!-- Lista de Instâncias -->
      <div v-if="instances.length > 0" class="instances-list">
        <div 
          v-for="instance in instances" 
          :key="instance.id"
          class="instance-card"
          :class="{ 
            'status-connected': instance.status === 'connected',
            'status-connecting': instance.status === 'connecting',
            'status-disconnected': instance.status === 'disconnected'
          }"
        >
          <div class="instance-header">
            <div class="instance-info">
              <h3 class="instance-name">
                <i class="fa-brands fa-whatsapp"></i>
                {{ instance.instanceName }}
              </h3>
              <div class="instance-details">
                <span :class="['status-badge', getStatusClass(instance.status)]">
                  <i :class="getStatusIcon(instance.status)"></i>
                  {{ instance.statusDescription }}
                </span>
                <span v-if="instance.phoneNumber" class="phone-badge">
                  <i class="fa-solid fa-phone"></i>
                  {{ formatPhoneNumber(instance.phoneNumber) }}
                </span>
              </div>
              <div class="instance-meta">
                <p v-if="instance.connectedAt" class="meta-item">
                  <i class="fa-solid fa-clock"></i>
                  Conectado em: {{ formatDate(instance.connectedAt) }}
                </p>
                <p class="meta-item">
                  <i class="fa-solid fa-calendar"></i>
                  Criado em: {{ formatDate(instance.createdAt) }}
                </p>
              </div>
            </div>
            <div class="instance-actions">
              <button 
                v-if="instance.status === 'disconnected'"
                class="btn-action btn-connect" 
                @click="getQrCode(instance.id)"
                title="Conectar"
              >
                <i class="fa-solid fa-qrcode"></i>
                Obter QR Code
              </button>
              <button 
                v-if="instance.status === 'connecting'"
                class="btn-action btn-check" 
                @click="checkConnection(instance.id)"
                title="Verificar Conexão"
              >
                <i class="fa-solid fa-sync"></i>
                Verificar
              </button>
              <button 
                v-if="instance.status === 'connected'"
                class="btn-action btn-disconnect" 
                @click="disconnectInstance(instance.id)"
                title="Desconectar"
              >
                <i class="fa-solid fa-unlink"></i>
                Desconectar
              </button>
              <button 
                class="btn-action btn-edit" 
                @click="editInstance(instance)"
                title="Editar"
              >
                <i class="fa-solid fa-pen-to-square"></i>
              </button>
              <button 
                class="btn-action btn-delete" 
                @click="deleteInstance(instance.id)"
                title="Excluir"
              >
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>

          <!-- QR Code -->
          <div v-if="instance.qrCode && instance.status === 'connecting'" class="qr-code-section">
            <p class="qr-instructions">
              <i class="fa-solid fa-info-circle"></i>
              Escaneie o QR Code abaixo com o WhatsApp:
            </p>
            <div class="qr-code-container">
              <div :id="`qr-code-${instance.id}`" class="qr-code-display"></div>
            </div>
            <p class="qr-hint">
              Abra o WhatsApp no celular → Menu (3 pontos) → Aparelhos conectados → Conectar um aparelho
            </p>
          </div>
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-else class="empty-state">
        <i class="fa-brands fa-whatsapp"></i>
        <p>Nenhuma instância cadastrada</p>
        <button type="button" class="btn-primary" @click="createInstance()">
          <i class="fa-solid fa-plus"></i>
          Criar Primeira Instância
        </button>
      </div>

      <!-- Formulário de Instância -->
      <div v-if="editingInstance" class="instance-form">
        <div class="form-header">
          <h4 class="form-title">
            <i class="fa-solid fa-edit"></i>
            {{ currentInstance.id ? 'Editar Instância' : 'Nova Instância' }}
          </h4>
          <button class="btn-close" @click="cancelEdit">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="form-body">
          <div class="form-group">
            <label class="form-label">
              <i class="fa-solid fa-tag"></i>
              Nome da Instância *
            </label>
            <input 
              type="text" 
              class="form-input"
              v-model="currentInstance.instanceName"
              placeholder="Ex: instancia-principal"
              maxlength="100"
            />
            <small class="form-hint">
              <i class="fa-solid fa-info-circle"></i>
              Nome único para identificar esta instância (sem espaços, apenas letras, números e hífens)
            </small>
          </div>

          <div class="form-group">
            <label class="form-label">
              <i class="fa-solid fa-toggle-on"></i>
              Status
            </label>
            <div class="toggle-container">
              <label class="toggle-switch">
                <input 
                  type="checkbox" 
                  v-model="currentInstance.isActive"
                />
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-label">
                {{ currentInstance.isActive ? 'Ativa' : 'Inativa' }}
              </span>
            </div>
          </div>
        </div>

        <div class="form-footer">
          <button type="button" class="btn-primary" @click="saveInstance()">
            <i class="fa-solid fa-save"></i>
            Salvar
          </button>
          <button type="button" class="btn-secondary" @click="cancelEdit">
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { useToast } from "@/composables/useToast";
import { useConfirm } from "@/composables/useConfirm";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";

// QRCode.js para gerar QR Code no frontend
let QRCode = null;
if (typeof window !== 'undefined') {
  // Carregar QRCode.js dinamicamente
  const script = document.createElement('script');
  script.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
  script.onload = () => {
    QRCode = window.QRCode;
  };
  document.head.appendChild(script);
}

export default {
  name: "ViewWhatsAppInstance",
  setup() {
    const { success, error, warning, info } = useToast();
    const { confirm } = useConfirm();
    return { 
      toast: { success, error, warning, info },
      confirm 
    };
  },
  data() {
    return {
    loading: true,
    instances: [],
    editingInstance: false,
    currentInstance: {
      id: null,
      instanceName: "",
      isActive: true
    },
    qrCodeRefreshInterval: null
    };
  },
  async mounted() {
    this.loading = true;
    await this.loadInstances();
  },
  beforeUnmount() {
    if (this.qrCodeRefreshInterval) {
      clearInterval(this.qrCodeRefreshInterval);
    }
  },
  methods: {
    async loadInstances() {
      this.loading = true;
      try {
        const response = await mainService.searchWhatsAppInstances({});
        this.instances = response.data || [];
        
        // Gerar QR Codes para instâncias que têm QR Code
        this.$nextTick(() => {
          this.instances.forEach(instance => {
            if (instance.qrCode && instance.status === 'connecting') {
              this.generateQRCode(instance.id, instance.qrCode);
            }
          });
        });
        
        // Verificar conexões periodicamente para instâncias em "connecting"
        this.startConnectionCheck();
      } catch (error) {
        this.toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    startConnectionCheck() {
      if (this.qrCodeRefreshInterval) {
        clearInterval(this.qrCodeRefreshInterval);
      }
      
      const connectingInstances = this.instances.filter(i => i.status === 'connecting');
      if (connectingInstances.length > 0) {
        this.qrCodeRefreshInterval = setInterval(async () => {
          for (const instance of connectingInstances) {
            await this.checkConnection(instance.id, true);
          }
        }, 5000); // Verificar a cada 5 segundos
      }
    },
    createInstance() {
      this.currentInstance = {
        id: null,
        instanceName: "",
        isActive: true
      };
      this.editingInstance = true;
    },
    editInstance(instance) {
      this.currentInstance = {
        id: instance.id,
        instanceName: instance.instanceName,
        isActive: instance.isActive
      };
      this.editingInstance = true;
    },
    cancelEdit() {
      this.editingInstance = false;
      this.currentInstance = {
        id: null,
        instanceName: "",
        isActive: true
      };
    },
    async saveInstance() {
      if (!this.currentInstance.instanceName.trim()) {
        this.toast.error("O nome da instância é obrigatório!");
        return;
      }

      // Validar formato do nome (sem espaços, apenas letras, números e hífens)
      if (!/^[a-zA-Z0-9-]+$/.test(this.currentInstance.instanceName)) {
        this.toast.error("O nome da instância deve conter apenas letras, números e hífens (sem espaços)!");
        return;
      }

      this.loading = true;
      try {
        // Garantir que o instanceName está preenchido
        const instanceData = {
          instanceName: this.currentInstance.instanceName.trim(),
          isActive: this.currentInstance.isActive
        };
        
        if (this.currentInstance.id) {
          instanceData.id = this.currentInstance.id;
          await mainService.updateWhatsAppInstance(instanceData);
          this.toast.success("Instância atualizada com sucesso!");
        } else {
          // Criar nova instância
          const response = await mainService.insertWhatsAppInstance(instanceData);
          this.toast.success("Instância criada com sucesso!");
          
          // Se a instância foi criada, tentar obter QR Code automaticamente
          if (response.data && response.data.id) {
            await this.getQrCode(response.data.id);
          }
        }
        await this.loadInstances();
        this.cancelEdit();
      } catch (error) {
        this.toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    async deleteInstance(id) {
      try {
        const confirmed = await this.confirm({
          title: 'Excluir Instância',
          message: 'Confirma a exclusão desta instância? A instância será removida do Evolution API também.'
        });

        if (confirmed) {
          this.loading = true;
          try {
            await mainService.deleteWhatsAppInstance(id);
            this.toast.success("Instância excluída com sucesso!");
            await this.loadInstances();
          } catch (error) {
            this.toast.error(handleErrorMessage(error));
          } finally {
            this.loading = false;
          }
        }
      } catch {
        // Usuário cancelou
      }
    },
    async getQrCode(instanceId) {
      this.loading = true;
      try {
        const response = await mainService.getWhatsAppInstanceQrCode(instanceId);
        if (response.data) {
          this.toast.success("QR Code obtido! Escaneie com o WhatsApp.");
          
          // Gerar QR Code visualmente
          this.$nextTick(() => {
            this.generateQRCode(instanceId, response.data);
          });
          
          await this.loadInstances();
          
          // Iniciar verificação automática de conexão
          this.startConnectionCheck();
        } else {
          this.toast.error("Não foi possível obter o QR Code. Tente novamente.");
        }
      } catch (error) {
        this.toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    generateQRCode(instanceId, qrString) {
      if (!qrString) return;
      
      const qrContainer = document.getElementById(`qr-code-${instanceId}`);
      if (!qrContainer) return;
      
      // Limpa o QR Code anterior
      qrContainer.innerHTML = '';
      
      // Se o qrString é base64 ou data URL, mostra como imagem
      if (qrString.startsWith('data:image') || (qrString.length < 200 && !qrString.startsWith('http'))) {
        const img = document.createElement('img');
        img.src = qrString.startsWith('data:image') ? qrString : `data:image/png;base64,${qrString}`;
        img.style.maxWidth = '300px';
        img.style.height = 'auto';
        img.style.display = 'block';
        img.style.margin = '0 auto';
        qrContainer.appendChild(img);
      } else {
        // Gera QR Code a partir da string usando QRCode.js
        if (window.QRCode) {
          try {
            new window.QRCode(qrContainer, {
              text: qrString,
              width: 300,
              height: 300,
              colorDark: "#000000",
              colorLight: "#ffffff",
              correctLevel: window.QRCode.CorrectLevel.L
            });
          } catch (error) {
            console.error('Erro ao gerar QR Code:', error);
            qrContainer.innerHTML = '<p style="color: red; text-align: center;">Erro ao gerar QR Code</p>';
          }
        } else {
          // Fallback: mostra a string se QRCode.js não estiver disponível
          qrContainer.innerHTML = `<p style="text-align: center; word-break: break-all;">${qrString.substring(0, 100)}...</p>`;
        }
      }
    },
    async checkConnection(instanceId, silent = false) {
      if (!silent) {
        this.loading = true;
      }
      try {
        const response = await mainService.connectWhatsAppInstance(instanceId);
        if (response.data) {
          if (!silent) {
            this.toast.success("Instância conectada com sucesso!");
          }
          await this.loadInstances();
          
          // Parar polling se conectado
          const instance = this.instances.find(i => i.id === instanceId);
          if (instance && instance.status === 'connected') {
            this.stopConnectionCheck();
          }
        } else {
          if (!silent) {
            this.toast.warning("A instância ainda não está conectada. Continue escaneando o QR Code.");
          }
        }
      } catch (error) {
        if (!silent) {
          this.toast.error(handleErrorMessage(error));
        }
      } finally {
        if (!silent) {
          this.loading = false;
        }
      }
    },
    stopConnectionCheck() {
      if (this.qrCodeRefreshInterval) {
        clearInterval(this.qrCodeRefreshInterval);
        this.qrCodeRefreshInterval = null;
      }
    },
    async disconnectInstance(instanceId) {
      try {
        const confirmed = await this.confirm({
          title: 'Desconectar Instância',
          message: 'Confirma a desconexão desta instância?'
        });

        if (confirmed) {
          this.loading = true;
          try {
            await mainService.disconnectWhatsAppInstance(instanceId);
            this.toast.success("Instância desconectada com sucesso!");
            await this.loadInstances();
          } catch (error) {
            this.toast.error(handleErrorMessage(error));
          } finally {
            this.loading = false;
          }
        }
      } catch {
        // Usuário cancelou
      }
    },
    getStatusClass(status) {
      switch (status) {
        case 'connected':
          return 'badge-success';
        case 'connecting':
          return 'badge-warning';
        case 'disconnected':
          return 'badge-danger';
        default:
          return 'badge-secondary';
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case 'connected':
          return 'fa-solid fa-check-circle';
        case 'connecting':
          return 'fa-solid fa-sync fa-spin';
        case 'disconnected':
          return 'fa-solid fa-times-circle';
        default:
          return 'fa-solid fa-question-circle';
      }
    },
    formatDate(date) {
      if (!date) return "";
      return jsFunctions.setDateTimeDisplay(date);
    },
    formatPhoneNumber(phone) {
      if (!phone) return "";
      const cleaned = phone.replace(/\D/g, "");
      if (cleaned.length >= 13) {
        return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
      }
      return phone;
    }
  }
};
</script>

<style scoped>
.whatsapp-instance-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  background: var(--bg-primary);
}

.header {
  margin-bottom: 30px;
}

.header h1 {
  font-size: 2rem;
  color: var(--text-primary);
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.header h1 i {
  color: #25D366;
}

.subtitle {
  color: var(--text-secondary);
  margin: 0;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--text-muted);
}

.action-bar {
  margin-bottom: 1.5rem;
}

.instances-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.instance-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid var(--card-border);
  transition: var(--transition);
  box-shadow: var(--shadow-md);
}

.instance-card:hover {
  box-shadow: var(--shadow-md);
}

.instance-card.status-connected {
  border-color: #25D366;
  background: rgba(37, 211, 102, 0.1);
}

.instance-card.status-connecting {
  border-color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
}

.instance-card.status-disconnected {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

[data-theme="dark"] .instance-card.status-connected {
  background: rgba(37, 211, 102, 0.15);
}

[data-theme="dark"] .instance-card.status-connecting {
  background: rgba(245, 158, 11, 0.15);
}

[data-theme="dark"] .instance-card.status-disconnected {
  background: rgba(239, 68, 68, 0.15);
}

.instance-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.instance-info {
  flex: 1;
}

.instance-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.75rem 0;
}

.instance-name i {
  color: #25D366;
}

.instance-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
}

.badge-success {
  background: rgba(16, 185, 129, 0.2);
  color: var(--success);
}

.badge-warning {
  background: rgba(245, 158, 11, 0.2);
  color: var(--warning);
}

.badge-danger {
  background: rgba(239, 68, 68, 0.2);
  color: var(--danger);
}

.badge-secondary {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

[data-theme="dark"] .badge-success {
  background: rgba(16, 185, 129, 0.25);
}

[data-theme="dark"] .badge-warning {
  background: rgba(245, 158, 11, 0.25);
}

[data-theme="dark"] .badge-danger {
  background: rgba(239, 68, 68, 0.25);
}

.phone-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color);
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

[data-theme="dark"] .phone-badge {
  background: rgba(96, 165, 250, 0.2);
}

.instance-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0;
}

.instance-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.btn-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--card-background);
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
}

.btn-action:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

[data-theme="dark"] .btn-action:hover {
  box-shadow: var(--shadow-md);
}

.btn-connect {
  color: #25D366;
  border-color: #25D366;
}

.btn-connect:hover {
  background: #25D366;
  color: white;
}

.btn-check {
  color: #f59e0b;
  border-color: #f59e0b;
}

.btn-check:hover {
  background: #f59e0b;
  color: white;
}

.btn-disconnect {
  color: #ef4444;
  border-color: #ef4444;
}

.btn-disconnect:hover {
  background: #ef4444;
  color: white;
}

.btn-edit {
  color: #3b82f6;
  border-color: #3b82f6;
}

.btn-edit:hover {
  background: #3b82f6;
  color: white;
}

.btn-delete {
  color: #ef4444;
  border-color: #ef4444;
}

.btn-delete:hover {
  background: #ef4444;
  color: white;
}

.qr-code-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid var(--border-color);
}

.qr-instructions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
}

.qr-code-container {
  display: flex;
  justify-content: center;
  padding: 1rem;
  background: var(--input-background);
  border-radius: 8px;
  border: 2px solid var(--border-color);
  margin-bottom: 1rem;
}

.qr-code-display {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.qr-code-display img {
  max-width: 300px;
  height: auto;
  display: block;
}

.qr-hint {
  font-size: 0.875rem;
  color: var(--text-muted);
  text-align: center;
  margin: 0;
  font-style: italic;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
  color: #25D366;
}

.instance-form {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 30px;
  margin-top: 25px;
  border: 1px solid var(--card-border);
  box-shadow: var(--shadow-md);
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.form-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 1rem;
  transition: var(--transition);
  background: var(--input-background);
  color: var(--text-primary);
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
  background: var(--input-background);
}

[data-theme="dark"] .form-input:focus {
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.2);
}

.form-hint {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--bg-tertiary);
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: var(--card-background);
  transition: 0.3s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #25D366;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

.toggle-label {
  font-weight: 500;
  color: var(--text-primary);
}

.form-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
}

.btn {
  padding: 12px 25px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #25D366;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #20ba5a;
  transform: translateY(-1px);
}

.btn-secondary {
  background: var(--secondary-btn-color);
  color: white;
}

.btn-secondary:hover {
  background: var(--secondary-btn-hover);
}
</style>

